import { ReactNode, useEffect } from 'react'
import { Navigate, useLocation } from 'react-router'
import { observer } from 'mobx-react-lite'
import { Loader2 } from 'lucide-react'

import { useStores } from '@/components/StoreProvider'

interface ProtectedRouteProps {
  children: ReactNode
}

const ProtectedRoute = observer(({ children }: ProtectedRouteProps) => {
  const location = useLocation()
  const { userStore, settingsStore } = useStores()

  useEffect(() => {
    if (!userStore.isLoading && !userStore.isAuthenticated) {
      userStore.checkAuth()
    }
  }, [userStore])

  useEffect(() => {
    // Fetch settings when user is authenticated and settings haven't been loaded
    if (userStore.isAuthenticated && !settingsStore.isLoading && !settingsStore.settingsLoaded) {
      settingsStore.fetchLLMSettings()
    }
  }, [userStore.isAuthenticated, settingsStore])

  if (userStore.isLoading || settingsStore.isLoading || (userStore.isAuthenticated && !settingsStore.settingsLoaded)) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-gray-500" />
      </div>
    )
  }

  if (!userStore.isAuthenticated) {
    // Redirect to auth page with return URL
    return <Navigate to="/auth" state={{ from: location }} replace />
  }

  // Check if user is onboarded (only after settings are loaded)
  if (settingsStore.settingsLoaded && !settingsStore.onboarded && location.pathname !== '/welcome') {
    // Allow access to /welcome page
    return <Navigate to="/welcome" replace />
  }

  return <>{children}</>
})

export default ProtectedRoute
